import { t } from "@travily/i18n";
import type Program from "@travily/types/models/ProgramsCard";
import type { ProgramsCardPhotosTrait } from "@travily/types/models/ProgramsCard";
import { CardCarousel } from "@ui/components/CardCarousel";
import { CardGrid } from "@ui/components/CardGrid";
import { EmptyState } from "@ui/components/EmptyState";
import { Heading } from "@ui/components/Heading";
import { Illustration } from "@ui/components/Illustration";
import { OutlineButton } from "@ui/components/OutlineButton";
import { useState } from "react";
import { ProgramCard } from "@/components/ProgramCard/ProgramCard";

const PROGRAMS_PER_PAGE = 6;

export type CommunityProgram = Program & ProgramsCardPhotosTrait;

export interface ProgramsProps {
  communityDisplayName: string;
  owner?: boolean;
  programs: CommunityProgram[];
}

export function Programs({ communityDisplayName, owner, programs }: ProgramsProps) {
  const [visibleCount, setVisibleCount] = useState(PROGRAMS_PER_PAGE);
  const hasMore = programs.length > visibleCount;
  const desktopPrograms = programs.slice(0, visibleCount);

  return (
    <div className="mt-8 flex w-full flex-col gap-4 text-left lg:mt-12 lg:gap-6">
      <Heading.Root divider="responsive">
        <Heading.Title>{t("components.views.communities.programs.title")}</Heading.Title>
      </Heading.Root>
      {programs.length > 0 ? (
        <>
          <div className="md:hidden">
            <CardCarousel>
              {programs.map((program) => (
                <ProgramCard key={program.id} program={program} />
              ))}
            </CardCarousel>
          </div>
          <div className="hidden md:block">
            <CardGrid capCardWidth>
              {desktopPrograms.map((program) => (
                <ProgramCard key={program.id} program={program} />
              ))}
            </CardGrid>
          </div>
          {hasMore && (
            <OutlineButton
              className="hidden md:inline-flex md:self-center"
              onPress={() => setVisibleCount((prev) => prev + PROGRAMS_PER_PAGE)}
            >
              {t("common.actions.view_more")}
            </OutlineButton>
          )}
        </>
      ) : (
        <EmptyState
          description={
            owner
              ? t("components.views.communities.programs.empty_owner")
              : t("components.views.communities.programs.empty_visitor", { name: communityDisplayName })
          }
          illustration={<Illustration name="programs" />}
          title={t("components.views.communities.programs.empty_title")}
        />
      )}
    </div>
  );
}
